<template>
  <b-container v-if="fetching" class="h-100 text-center">
    <b-spinner />
  </b-container>
  <b-container v-else class="h-100">
    <b-row v-if="cartItems.length" class="justify-content-center h-100">
      <b-col align-self="start" cols="12">
        <b-col ref="text__cart-title" class="display-4 font-weight-bold mb-4 px-0">{{
          isCheckout ? 'Checkout' : 'Item cart'
        }}</b-col>
        <keep-alive>
          <component
            :is="stateComponent"
            :ref="stateComponent"
            v-model="selected"
            class="component__cart-deatil"
            :class="isCheckout ? 'checkout' : 'cartItemLists'"
            :cart-items="cartItems"
            @delete:success="fetchCartCount"
            @update:selectedPayment="handleUpdate"
          ></component>
        </keep-alive>
      </b-col>
      <b-col align-self="end" cols="12" class="mt-4 mb-5">
        <b-form-checkbox v-if="isCheckout" ref="checkbox__terms" v-model="isConsent" class="mb-2">
          <router-link target="_blank" :to="{ name: 'payment-policy' }">
            Terms and Conditions
          </router-link>
        </b-form-checkbox>
        <hr class="my-4" />
        <b-button
          v-if="!isCheckout"
          ref="button__checkout"
          variant="success"
          class="button__checkout"
          :disabled="selected.length == 0"
          @click.prevent="onCheckout"
        >
          Checkout
        </b-button>

        <div v-else>
          <b-button
            ref="button__make-payment"
            variant="success"
            class="button__make-payment mr-3"
            :disabled="isPayment || !isConsent"
            @click.prevent="onPayment"
          >
            <span class="d-flex align-items-center">
              <b-spinner v-if="isPayment" small class="mr-2"></b-spinner>
              Make Payment
            </span>
          </b-button>

          <b-button
            ref="button__cancel"
            variant="link"
            class="button__cancel"
            :disabled="isPayment"
            @click.prevent="onCancel"
          >
            Cancel
          </b-button>
        </div>
      </b-col>
    </b-row>
    <b-row v-else class="justify-content-center h-100">
      <b-col align-self="center" cols="12">
        <b-col ref="text__cart-empty" class="display-4 font-weight-bold mb-4 px-0 text-center"
          >Your cart is empty</b-col
        >
        <b-col class="text-black-50 text-center"
          ><p ref="text__cart-empty-detail">You have not added anything to your cart yet</p></b-col
        >
        <b-col class="text-center">
          <router-link :to="{ name: 'learning-unit' }">
            <b-button
              ref="button__explore-item"
              variant="primary px-5"
              class="button__explore-item"
            >
              Explore Item
            </b-button>
          </router-link>
        </b-col>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import CartItemLists from '@/views/Cart/components/CartItemLists';
import Checkout from '@/views/Cart/components/Checkout';
import Cart from '@/models/Cart';
import CartItem from '@/models/CartItem';
import { userInfoMixin } from '@/mixins/userInfoMixin';
export default {
  name: 'PageCartItem',

  components: {
    cartItemLists: CartItemLists,
    checkout: Checkout
  },

  mixins: [userInfoMixin],

  data() {
    return {
      selected: [],
      fetching: true,
      isCheckout: false,
      isPayment: false,
      isConsent: false,
      paymentMethod: null
    };
  },

  computed: {
    userCart() {
      return Cart.query().where('user_id', this.user.id).first();
    },

    cartItems() {
      return this.userCart
        ? CartItem.query()
            .where('cart_id', this.userCart.id)
            .with('cart')
            .with('learning_unit')
            .with('learning_unit_option')
            .orderBy('created_at', 'desc')
            .get()
        : [];
    },

    stateComponent() {
      return this.isCheckout ? 'checkout' : 'cartItemLists';
    }
  },

  async mounted() {
    try {
      this.fetching = true;
      await this.fetchItemCart();
    } finally {
      this.fetching = false;
    }
  },

  methods: {
    async fetchItemCart() {
      if (!this.userId) return (this.fetching = false);
      await CartItem.deleteAll();
      await CartItem.api().fetchMyCartItem();
    },

    async onCheckout() {
      if (!this.userId || this.selected.length == 0) return;

      try {
        const {
          response: { data = [] }
        } = await CartItem.api().fetchMyCartItem({
          id_in: this.selected
        });

        this.selected = data
          .map((x) => {
            if (x.learning_unit.is_published && x.is_available) {
              return x.id;
            } else if (
              x.learning_unit_option.option_type == 'QUOTA' &&
              x.learning_unit_option.mode == 'VERIFY'
            ) {
              return x.id;
            }
          })
          .filter(Boolean);

        const isAllAvaliable = data.every((item) => item.is_available == true);
        const isVerifyQuota = data.every(
          (item) =>
            item.learning_unit_option.option_type == 'QUOTA' &&
            item.learning_unit_option.mode == 'VERIFY'
        );

        if (isAllAvaliable || isVerifyQuota) {
          //go to checkout
          this.isCheckout = true;
        } else {
          this.$toast.open({
            message: 'Some cart items have been changed, please try to checkout again.',
            type: 'warning',
            duration: 5000
          });
        }
      } catch (error) {
        this.$toast.open({
          message: 'Cannot load cart, please try to again.',
          type: 'error',
          duration: 5000
        });
      }
    },

    handleUpdate(value) {
      this.paymentMethod = value;
    },

    async onPayment() {
      if (this.$refs['checkout']) {
        this.$refs['checkout'].onSelectedPayment();
      }

      try {
        this.isPayment = true;

        //call api make payment
        const order = await Cart.api().checkout({
          item_ids: this.selected,
          payment_method: this.paymentMethod
        });

        this.fetchCartCount();

        this.fetchItemCart();

        if (order.status === 'SUCCESS') {
          this.$router.push({
            name: 'payment-success',
            params: { orderId: order.id }
          });
        }
      } catch (error) {
        this.$toast.open({
          message: 'Something went wrong, please try to make payment again.',
          type: 'error',
          duration: 5000
        });
      } finally {
        this.isPayment = false;
      }
    },

    async fetchCartCount() {
      if (!this.userId) return;
      const {
        response: { data }
      } = await Cart.api().fetchMyCountItems({}, false);

      Cart.insertOrUpdate({
        data: [data]
      });
    },
    onCancel() {
      this.isCheckout = false;
      this.isConsent = false;
    }
  }
};
</script>

<style lang="scss">
.text__action-delete {
  color: #f83245;
}

.text__discount-price {
  // color: #3c44b1;
  color: #3b3e66;
}

.cart__items-list > span:nth-child(1) hr {
  display: none;
}

.img-size {
  height: 100px;
  max-height: max-content;
}
</style>
